import { GoAiguaButton } from "components/generic";
import GoAiguaSelect from "components/generic/goaiguaselect";
import { useEffect, useState } from "react";
import {
    addUserRoles,
    getUserRoles,
    addUserToGroup,
    createRoleGroup,
    getRolesByGroupId,
    getRolesByProvenance,
    removeUserRoles,
} from "services/requests/v1/user";
import {
    GoAiguaSelectOption,
    SetRequestError,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic";
import GoAiguaTextSearch from "components/generic/goaiguatextinput";
import { useIntl } from "react-intl";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import { isTokenKCApiExpired } from "utils/functions";
import { useStore } from "react-redux";
import { refreshPage } from "utils/functions";

type UserAssigmentRoleType = {
    availableGroups: GoAiguaSelectOption[];
    provenance: string;
    userUid?: string;
    setOpenModal: (openModal: boolean) => void;
    setAvailableGroups: (availableGroups: GoAiguaSelectOption[]) => void;
    token: string;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
};

const UserAssigmentRole = ({
    availableGroups,
    provenance,
    userUid,
    setOpenModal,
    setAvailableGroups,
    token,
    setError,
    subscribeContent,
    unsubscribeContent,
}: UserAssigmentRoleType) => {
    const intl = useIntl();

    const [selectedGroup, setSelectedGroup] = useState<GoAiguaSelectOption>({
        value: "",
        label: "",
    });

    const [isChecked, setIsChecked] = useState<boolean>(false);

    const [rolesGroup, setRolesGroup] = useState<
        { id: number; name: string }[]
    >([]);

    const [rolesCompare, setRolesCompare] = useState<any[]>([]);

    const [allRoles, setAllRoles] = useState<{ id: number; name: string }[]>(
        []
    );

    const [availableRoles, setAvailableRoles] = useState<
        { id: number; name: string }[]
    >([]);

    const [selectedRoles, setSelectedRoles] = useState<
        { id: number; name: string }[]
    >([]);

    const [asignedRoles, setAsignedRoles] = useState<
        { id: number; name: string }[]
    >([]);

    const [groupName, setGroupName] = useState<string>("");

    const store = useStore();
    const currentUserUid = store.getState().keycloak.sub;

    const {
        keycloak,
    }: {
        initialized: boolean;
        keycloak: Keycloak.KeycloakInstance;
    } = useKeycloak();

    const checkIfTokenIsValid = () => {
        if (isTokenKCApiExpired(token)) {
            keycloak.logout();
        }
    };

    useEffect(() => {
        checkIfTokenIsValid();
        // Depends on every action that will need KC API Call
    }, [token, availableRoles, selectedRoles, asignedRoles]);

    useEffect(() => {
        checkIfTokenIsValid();
        getRolesByProvenance(provenance).then((res) => {
            setAllRoles(
                res.data.map((role: any) => {
                    return { id: role.id, name: role.name };
                })
            );
        });
    }, []);

    useEffect(() => {
        checkIfTokenIsValid();
        if (userUid && allRoles.length > 0) {
            getUserRoles(userUid, provenance).then((res) =>
                addSelectedFromGroup(
                    res.data.map((role: { name: string }) => role.name)
                )
            );
        }
    }, [allRoles]);

    useEffect(() => {
        checkIfTokenIsValid();
        if (selectedGroup.value === "-1") {
            setAvailableRoles(allRoles.slice());
            setAsignedRoles([]);
        } else if (selectedGroup.value !== "" && selectedGroup.value !== "-1") {
            getRolesByGroupId(+selectedGroup.value).then((res) => {
                addSelectedFromGroup(res.data.roles);
            });
        } else {
            setAsignedRoles([]);
        }
    }, [selectedGroup, allRoles]);

    useEffect(() => {
        setRolesCompare(
            getDifference(asignedRoles, rolesGroup).concat(
                getDifference(rolesGroup, asignedRoles)
            )
        );
        setIsChecked(false);
    }, [asignedRoles, availableRoles]);

    const addSelected = () => {
        let asignedRolesAux = asignedRoles;
        asignedRolesAux = asignedRolesAux.concat(selectedRoles);
        const asignedRolesSet = new Set(asignedRolesAux);

        setAsignedRoles([...asignedRolesSet]);
        setSelectedRoles([]);

        const availableRolesAux = availableRoles;
        selectedRoles.forEach((role) => {
            const indexRole = availableRoles.indexOf(role);
            if (indexRole !== -1) {
                availableRolesAux.splice(indexRole, 1);
            }
        });
        setAvailableRoles(availableRolesAux);
    };

    const removeSelected = () => {
        let availableRolesAux = availableRoles;
        availableRolesAux = availableRolesAux.concat(selectedRoles);
        const availableRolesSet = new Set(availableRolesAux);

        setAvailableRoles([...availableRolesSet]);
        setSelectedRoles([]);

        const asignedRolesAux = asignedRoles;
        selectedRoles.forEach((role) => {
            const indexRole = asignedRoles.indexOf(role);
            if (indexRole !== -1) {
                asignedRolesAux.splice(indexRole, 1);
            }
        });
        setAsignedRoles(asignedRolesAux);
    };

    const addSelectedFromGroup = (selectedRolesFromGroup: string[]) => {
        const rolesFormatted = allRoles
            .slice()
            .filter((role) => selectedRolesFromGroup.includes(role.name));
        if (rolesFormatted) {
            setRolesGroup(rolesFormatted);
            const asignedRolesSet = new Set(rolesFormatted);

            setAsignedRoles([...asignedRolesSet]);
            setSelectedRoles([]);
        }

        const availableRoleArray = allRoles.slice();

        selectedRolesFromGroup.forEach((role) => {
            const indexRole = availableRoleArray.findIndex(
                (e) => e.name === role
            );
            if (indexRole !== -1) {
                availableRoleArray.splice(indexRole, 1);
            }
        });
        setAvailableRoles(availableRoleArray);
    };

    const handleChange = () => setIsChecked(!isChecked);

    function getDifference(array1: any, array2: any) {
        return array1.filter((object1: any) => {
            return !array2.some((object2: any) => {
                return object1.id === object2.id;
            });
        });
    }

    const createGroupRole = () => {
        return (
            <div className="c-user-assigment__role-panel__new-group">
                <div className="c-user-assigment__role-panel__new-group__checkbox">
                    <label>
                        <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={handleChange}
                        />
                        {intl.formatMessage({
                            id: "app.Component.Roles.CreateGroup",
                        })}
                    </label>
                </div>
                {isChecked && (
                    <div className="c-user-assigment__role-panel__new-group__group-name">
                        <label>
                            {intl.formatMessage({
                                id: "app.Component.Roles.GroupName",
                            })}
                        </label>
                        <GoAiguaTextSearch
                            text={groupName}
                            className={
                                "c-user-assigment__role-panel__new-group__group-name__input"
                            }
                            onChange={(value) => {
                                setGroupName(value);
                            }}
                            placeholder={intl.formatMessage({
                                id: "app.Component.Roles.Fill",
                            })}
                        />
                    </div>
                )}
            </div>
        );
    };

    const handleSave = () => {
        checkIfTokenIsValid();

        const contentName = "assingUser";
        subscribeContent("assignUser");

        if (groupName.length > 0 && isChecked) {
            let asignedRolesObject: { name: any }[] = [];
            asignedRoles.map((e) => {
                asignedRolesObject.push(e);
            });

            createRoleGroup(provenance, groupName, asignedRolesObject)
                .then((res) => {
                    if (userUid) {
                        let newGroups = availableGroups.concat([
                            {
                                value: "" + res.data.id,
                                label: res.data.name,
                            },
                        ]);

                        setAvailableGroups(newGroups);
                        addUserToGroup(res.data.id, userUid).then((res) => {
                            if (userUid === currentUserUid) {
                                refreshPage();
                            }
                            unsubscribeContent(contentName, true);
                            setOpenModal(false);
                        });
                    }
                })
                .catch(() => setError(contentName));
        } else {
            if (userUid) {
                addUserRoles(userUid, provenance, asignedRoles)
                    .then((res) => {
                        if (userUid === currentUserUid) {
                            refreshPage();
                        }
                        unsubscribeContent(contentName, true);
                        setOpenModal(false);
                    })
                    .catch(() => setError(contentName));
            }
        }

        if (userUid) {
            removeUserRoles(userUid, provenance, availableRoles);
        }
    };

    return (
        <>
            <div className={"c-user-assigment__role-panel"}>
                <div className={"c-user-assigment__role-panel__title"}>
                    {intl.formatMessage({
                        id: "app.Component.Roles.RolesAssignments",
                    })}
                    <hr />
                </div>
                <div className={"c-user-assigment__role-panel__group-selector"}>
                    <div
                        className={
                            "c-user-assigment__role-panel__group-selector__title"
                        }
                    >
                        {intl.formatMessage({
                            id: "app.Components.Roles.Group",
                        })}
                    </div>
                    <GoAiguaSelect
                        options={availableGroups}
                        selectedOption={selectedGroup.value}
                        setSelectedOption={(e) => {
                            const groupSelected = availableGroups.find(
                                (group) => group.value === e
                            );
                            if (groupSelected) {
                                setSelectedGroup(groupSelected);
                            }
                        }}
                        placeholder={intl.formatMessage({
                            id: "app.Component.Roles.SelectAnOption",
                        })}
                    />
                </div>
                <div className={"c-user-assigment__role-panel__roles-selector"}>
                    <div
                        className={
                            "c-user-assigment__role-panel__roles-selector__container"
                        }
                    >
                        <div
                            className={
                                "c-user-assigment__role-panel__roles-selector__container__title"
                            }
                        >
                            {intl.formatMessage({
                                id: "app.Component.Roles.AvailableRoles",
                            })}
                        </div>
                        <div
                            className={
                                "c-user-assigment__role-panel__roles-selector__container__list"
                            }
                        >
                            {availableRoles &&
                                availableRoles.map((role, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className={`c-user-assigment__role-panel__roles-selector__container__list-element${
                                                selectedRoles.includes(role)
                                                    ? "__selected"
                                                    : ""
                                            }`}
                                            onClick={() => {
                                                const indexRole =
                                                    selectedRoles.indexOf(role);
                                                if (indexRole === -1) {
                                                    setSelectedRoles(
                                                        selectedRoles.concat([
                                                            role,
                                                        ])
                                                    );
                                                } else {
                                                    setSelectedRoles(
                                                        (currentList) => {
                                                            const updatedList =
                                                                currentList.slice();
                                                            updatedList.splice(
                                                                indexRole,
                                                                1
                                                            );
                                                            return updatedList;
                                                        }
                                                    );
                                                }
                                            }}
                                        >
                                            {role.name}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                    <div
                        className={
                            "c-user-assigment__role-panel__roles-selector__container"
                        }
                    >
                        <div
                            className={
                                "c-user-assigment__role-panel__roles-selector__container__title"
                            }
                        >
                            {intl.formatMessage({
                                id: "app.Component.Roles.AsignedRoles",
                            })}
                        </div>
                        <div
                            className={
                                "c-user-assigment__role-panel__roles-selector__container__list"
                            }
                        >
                            {asignedRoles &&
                                asignedRoles.map((role, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className={`c-user-assigment__role-panel__roles-selector__container__list-element${
                                                selectedRoles.includes(role)
                                                    ? "__selected"
                                                    : ""
                                            }`}
                                            onClick={() => {
                                                const indexRole =
                                                    selectedRoles.indexOf(role);
                                                if (indexRole === -1) {
                                                    setSelectedRoles(
                                                        selectedRoles.concat([
                                                            role,
                                                        ])
                                                    );
                                                } else {
                                                    setSelectedRoles(
                                                        (currentList) => {
                                                            const updatedList =
                                                                currentList.slice();
                                                            updatedList.splice(
                                                                indexRole,
                                                                1
                                                            );
                                                            return updatedList;
                                                        }
                                                    );
                                                }
                                            }}
                                        >
                                            {role.name}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>

                <div className={"c-user-assigment__role-panel__switch-buttons"}>
                    <div
                        className={
                            "c-user-assigment__role-panel__switch-buttons__left"
                        }
                    >
                        <GoAiguaButton
                            text={intl.formatMessage({
                                id: "app.Component.Roles.AddSelected",
                            })}
                            className={
                                "c-user-assigment__role-panel__switch-buttons__left-button"
                            }
                            onClick={() => addSelected()}
                        />
                    </div>
                    <div
                        className={
                            "c-user-assigment__role-panel__switch-buttons__right"
                        }
                    >
                        <GoAiguaButton
                            text={intl.formatMessage({
                                id: "app.Component.Roles.RemoveSelected",
                            })}
                            className={
                                "c-user-assigment__role-panel__switch-buttons__right-button"
                            }
                            onClick={() => removeSelected()}
                        />
                    </div>
                </div>
                {selectedGroup.value === "-1" &&
                    asignedRoles.length > 0 &&
                    createGroupRole()}
                {selectedGroup.value !== "-1" &&
                    rolesCompare.length > 0 &&
                    createGroupRole()}
            </div>

            <div className={"c-user-assigment__footer"}>
                <GoAiguaButton
                    text={intl.formatMessage({ id: "app.gis.cancel" })}
                    className={"c-user-assigment__footer__cancel-button"}
                    onClick={() => {
                        setOpenModal(false);
                    }}
                />
                <GoAiguaButton
                    text={intl.formatMessage({ id: "app.gis.save_changes" })}
                    className={"c-user-assigment__footer__save-button"}
                    onClick={handleSave}
                />
            </div>
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        token: state.keycloak.token,
    };
};
const mapDispatchToProps = (dispatch: Dispatch) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(UserAssigmentRole);
